<template>
    <div class="form-info" v-if="!isProcessing && loadedData">
        <div class="form-header">
            <div class="header-icon">
                <i class="icon-business">🏢</i>
            </div>
            <h2>Business Permit Application Details</h2>
            <p class="subtitle">View Application #{{ loadedData.application.application_id || 'N/A' }}</p>
            <div class="status-badge" :class="getStatusClass(loadedData.application.status)">
                {{ loadedData.application.status || 'No Status' }}
            </div>

            <!-- <ActionMenu @action-selected="onActionSelected" /> -->
        </div>
        <!-- Application Summary Card -->
        <div class="summary-card">
            <div class="summary-row">
                <div class="summary-item">
                    <span class="summary-label">Application Type</span>
                    <span class="summary-value">{{ loadedData.permitInfo.type || 'N/A' }}</span>
                </div>
                <div class="summary-item">
                    <span class="summary-label">Payment Mode</span>
                    <span class="summary-value">{{ loadedData.permitInfo.mode_of_payment || 'N/A' }}</span>
                </div>
                <div class="summary-item">
                    <span class="summary-label">BIN</span>
                    <span class="summary-value">{{ loadedData.application.bin || 'Not Assigned' }}</span>
                </div>
                <div class="summary-item">
                    <span class="summary-label">Application Date</span>
                    <span class="summary-value">{{ formatDate(loadedData?.application?.date_created) }}</span>
                </div>
            </div>
        </div>

        <!-- Registration Details -->
        <div class="form-section view-section">
            <div class="section-header">
                <div class="section-number">1</div>
                <h3>Registration Details</h3>
            </div>
            <div class="info-grid">
                <div class="info-item">
                    <label class="info-label">DTI/SEC/CDA Registration Date</label>
                    <p class="info-value">{{ formatDate(loadedData.permitInfo?.dti_sec_cda_reg_date) }}</p>
                </div>
                <div class="info-item">
                    <label class="info-label">Registration Number</label>
                    <p class="info-value">{{ loadedData.permitInfo.dti_sec_cda_reg_no || 'N/A' }}</p>
                </div>
            </div>
        </div>

        <!-- Business Information -->
        <div class="form-section view-section">
            <div class="section-header">
                <div class="section-number">2</div>
                <h3>Business Information</h3>
            </div>
            <div class="info-grid">
                <div class="info-item">
                    <label class="info-label">Organization Type</label>
                    <p class="info-value">{{ loadedData.permitInfo.form_of_org || 'N/A' }}</p>
                </div>
                <div class="info-item">
                    <label class="info-label">TIN Number</label>
                    <p class="info-value">{{ loadedData.permitInfo.tin || 'N/A' }}</p>
                </div>
                <div class="info-item">
                    <label class="info-label">Trade Name</label>
                    <p class="info-value">{{ loadedData.permitInfo.trade_name || 'N/A' }}</p>
                </div>
                <div class="info-item">
                    <label class="info-label">Business Name</label>
                    <p class="info-value business-name">{{ loadedData.permitInfo.business_name || 'N/A' }}</p>
                </div>
                <div class="info-item">
                    <label class="info-label">Franchise/IPO</label>
                    <p class="info-value">{{ loadedData.permitInfo.franchise_or_ipo || 'N/A' }}</p>
                </div>
            </div>
        </div>

        <!-- Main Office Address -->
        <div class="form-section view-section">
            <div class="section-header">
                <div class="section-number">3</div>
                <h3>Main Office Address</h3>
            </div>
            <div class="address-card">
                <p class="address-line" v-if="loadedData.permitInfo.unit_no">{{ loadedData.permitInfo.unit_no }}</p>
                <p class="address-line" v-if="loadedData.permitInfo.building_name">{{
                    loadedData.permitInfo.building_name }}</p>
                <p class="address-line" v-if="loadedData.permitInfo.block_no_lot_no">Block/Lot: {{
                    loadedData.permitInfo.block_no_lot_no }}</p>
                <p class="address-line" v-if="loadedData.permitInfo.street">{{ loadedData.permitInfo.street }}</p>
                <p class="address-line" v-if="loadedData.permitInfo.subdivision">{{ loadedData.permitInfo.subdivision }}
                </p>
                <p class="address-line">{{ formatLocation(loadedData.permitInfo) }}</p>
            </div>
        </div>

        <!-- Owner Information -->
        <div class="form-section view-section">
            <div class="section-header">
                <div class="section-number">4</div>
                <h3>Owner Information</h3>
            </div>
            <div class="owner-card">
                <div class="owner-header">
                    <div class="owner-avatar">
                        {{ getInitials(loadedData.permitInfo.owner_firstname, loadedData.permitInfo.owner_lastname) }}
                    </div>
                    <div class="owner-details">
                        <h4 class="owner-name">
                            {{ formatFullName(loadedData.permitInfo) }}
                        </h4>
                        <p class="owner-citizenship">{{ loadedData.permitInfo.owner_citizenship || 'N/A' }}</p>
                    </div>
                </div>
                <div class="info-grid">
                    <div class="info-item">
                        <label class="info-label">Gender</label>
                        <p class="info-value">{{ loadedData.permitInfo.owner_sex || 'N/A' }}</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Owner Address -->
        <div class="form-section view-section">
            <div class="section-header">
                <div class="section-number">5</div>
                <h3>Owner Address</h3>
            </div>
            <div class="address-card">
                <p class="address-line" v-if="loadedData.permitInfo.owner_unit_no">{{
                    loadedData.permitInfo.owner_unit_no }}</p>
                <p class="address-line" v-if="loadedData.permitInfo.owner_building_name">{{
                    loadedData.permitInfo.owner_building_name }}</p>
                <p class="address-line" v-if="loadedData.permitInfo.owner_block_no_lot_no">Block/Lot: {{
                    loadedData.permitInfo.owner_block_no_lot_no }}</p>
                <p class="address-line" v-if="loadedData.permitInfo.owner_street">{{ loadedData.permitInfo.owner_street
                }}</p>
                <p class="address-line" v-if="loadedData.permitInfo.owner_subdivision">{{
                    loadedData.permitInfo.owner_subdivision }}</p>
                <p class="address-line">{{ formatOwnerLocation(loadedData.permitInfo) }}</p>
            </div>
        </div>

        <!-- Contact Persons -->
        <div class="form-section view-section">
            <div class="section-header">
                <div class="section-number">6</div>
                <h3>Contact Persons</h3>
                <span class="contacts-count">{{ loadedData.contacts?.length || 0 }} contact(s)</span>
            </div>

            <div v-if="loadedData.contacts && loadedData.contacts.length > 0" class="contacts-grid-view">
                <div class="contact-card-view" v-for="(contact, index) in loadedData.contacts" :key="index">
                    <div class="contact-header-view">
                        <div class="contact-avatar-view">
                            {{ getInitials(contact.firstname, contact.lastname) }}
                        </div>
                        <div class="contact-info-view">
                            <h5 class="contact-name-view">
                                {{ formatContactName(contact) }}
                            </h5>
                            <p class="contact-role-view">Contact Person</p>
                        </div>
                        <div class="contact-index-view">#{{ index + 1 }}</div>
                    </div>

                    <div class="contact-details-view">
                        <div class="contact-detail-view">
                            <i class="bi bi-telephone"></i>
                            <span>{{ contact.mobile || 'N/A' }}</span>
                        </div>
                        <div class="contact-detail-view">
                            <i class="bi bi-envelope"></i>
                            <span>{{ contact.email || 'N/A' }}</span>
                        </div>
                        <div class="contact-detail-view">
                            <i class="bi bi-gender-ambiguous"></i>
                            <span>{{ contact.sex || 'N/A' }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="empty-state">
                <div class="empty-icon">📭</div>
                <p>No contacts added</p>
            </div>
        </div>

        <!-- Business Operations -->
        <div class="form-section view-section">
            <div class="section-header">
                <div class="section-number">7</div>
                <h3>Business Operations</h3>
            </div>

            <div class="operations-summary">
                <div class="operations-grid">
                    <div class="operation-item">
                        <label class="operation-label">Area (sqm)</label>
                        <p class="operation-value">{{ loadedData?.businessInfo?.area_sqm || '0' }}</p>
                    </div>
                    <div class="operation-item">
                        <label class="operation-label">Total Employees</label>
                        <p class="operation-value">{{ (parseInt(loadedData?.businessInfo?.no_of_employees) || 0) +
                            (parseInt(loadedData?.businessInfo?.no_females) || 0) }}</p>
                    </div>
                    <div class="operation-item">
                        <label class="operation-label">Delivery Vehicles</label>
                        <p class="operation-value">{{ loadedData?.businessInfo?.no_delivery_vehicles || '0' }}</p>
                    </div>
                    <div class="operation-item">
                        <label class="operation-label">Employees in LGU</label>
                        <p class="operation-value">{{ loadedData?.businessInfo?.no_emp_residing_lgu || '0' }}</p>
                    </div>
                    <div class="operation-item">
                        <label class="operation-label">Property Status</label>
                        <p class="operation-value">{{ loadedData?.businessInfo?.rental_or_owned || 'N/A' }}</p>
                    </div>
                </div>
            </div>

            <div class="sub-section">
                <h4>Operation Address</h4>
                <div class="address-card">
                    <p class="address-line" v-if="loadedData.businessInfo?.operation_unit_no">{{
                        loadedData.businessInfo?.operation_unit_no }}</p>
                    <p class="address-line" v-if="loadedData.businessInfo?.operation_building_name">{{
                        loadedData.businessInfo?.operation_building_name }}</p>
                    <p class="address-line" v-if="loadedData.businessInfo?.operation_block_no_lot_no">Block/Lot: {{
                        loadedData.businessInfo?.operation_block_no_lot_no }}</p>
                    <p class="address-line" v-if="loadedData.businessInfo?.operation_street">{{
                        loadedData.businessInfo?.operation_street }}</p>
                    <p class="address-line" v-if="loadedData.businessInfo?.operation_subdivision">{{
                        loadedData.businessInfo?.operation_subdivision }}</p>
                    <p class="address-line">{{ formatOperationLocation(loadedData.businessInfo) }}</p>
                </div>
            </div>
        </div>

        <!-- Line of Business -->
        <div class="form-section view-section">
            <div class="section-header">
                <div class="section-number">8</div>
                <h3>Line(s) of Business</h3>
            </div>

            <div v-if="loadedData.permitLineOfBusinesses && loadedData.permitLineOfBusinesses.length > 0"
                class="lob-list">
                <div class="lob-card" v-for="(lob, index) in loadedData.permitLineOfBusinesses" :key="index">
                    <div class="lob-header">
                        <div class="lob-type-badge"
                            :class="lob.main_or_other === 'Main' ? 'main-badge' : 'other-badge'">
                            {{ lob.main_or_other }}
                        </div>
                        <h5 class="lob-title">{{ lob.line_of_business }}</h5>
                    </div>
                    <div class="lob-details">
                        <div class="lob-detail">
                            <label>Sub Category:</label>
                            <span>{{ lob.sub_category || 'N/A' }}</span>
                        </div>
                        <div class="lob-detail">
                            <label>Products/Services:</label>
                            <span>{{ lob.product_services || 'N/A' }}</span>
                        </div>
                        <div class="lob-detail">
                            <label>No. of Units:</label>
                            <span>{{ lob.no_units || '0' }}</span>
                        </div>
                        <div class="lob-detail">
                            <label>{{ lob.type === 'Renewal' ? 'Gross Sales' : 'Capitalization:' }}</label>
                            <span class="capitalization">₱{{ formatCurrency(lob.capitalization) }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="empty-state">
                <div class="empty-icon">📊</div>
                <p>No line of business added</p>
            </div>
        </div>

        <!-- Soa & Balances -->
        <div class="form-section view-section">
            <div class="section-header">
                <div class="section-number">9</div>
                <h3>Actions</h3>
            </div>
            <div class="certs-card">
                <div class="table-responsive">
                    <table class="table align-middle">
                        <thead>
                            <tr>
                                <th colspan="9">Balance</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="bal, index in loadedData.soaBalances" :key="`sb${bal.sb_id}`">
                                <td>{{ formatDate(bal?.due_date) }}</td>
                                <td>SOA AMT: <strong>{{ formatCurrency(bal.soaAmount) }}</strong></td>
                                <td>BTAX: <strong>{{ formatCurrency(bal.amount) }}</strong></td>
                                <td>{{ bal.mode_of_payment }}</td>
                                <td :class="bal.paid === 1 ? 'text-success' : 'text-danger'">{{ bal.paid === 1 ? 'PAID'
                                    :
                                    'UNPAID' }}</td>
                                <td>
                                    <button type="button" class="btn btn-sm btn-secondary me-1"
                                        @click="generateSoaBalance(bal.sb_id)"
                                        v-if="canGenerateSoa(bal, index)">Generate Soa</button>
                                    <button v-if="canAddArrears" @click="handleAddArrearsRequest" type="button"
                                        class="btn btn-sm btn-warning me-1">Add Arrears</button>
                                    <button v-if="bal?.soaAmount > 0 && bal.paid === 0" @click="handlePayNow(bal)"
                                        type="button" class="btn btn-sm btn-danger me-1">Pay Now</button>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td class="text-end" colspan="6">
                                    <div class="action-dropdown">
                                        <button class="btn btn-primary dropdown-toggle" type="button"
                                            data-bs-toggle="dropdown" aria-expanded="false">
                                            <i class="bi bi-gear me-1"></i> Actions
                                        </button>
                                        <ul class="dropdown-menu">

                                            <li v-if="canViewSoa">
                                                <a class="dropdown-item" href="#" @click.prevent="toggleSoaModal">
                                                    <i class="bi bi-eye me-2"></i> View TOP
                                                </a>
                                            </li>
                                            <li v-if="hasRoles(['admin', 'bplo'])">
                                                <a class="dropdown-item" href="#" @click.prevent="viewBusinessPermit">
                                                    <i class="bi bi-file-text me-2"></i> Business Permit
                                                </a>
                                            </li>

                                            <li>
                                                <hr class="dropdown-divider">
                                            </li>

                                            <!-- Export Section -->
                                            <li>
                                                <h6 class="dropdown-header">
                                                    <i class="bi bi-download me-1"></i> Export
                                                </h6>
                                            </li>
                                            <!-- <li>
                                                <a class="dropdown-item" href="#" @click.prevent="exportPDF">
                                                    <i class="bi bi-file-pdf me-2 text-danger"></i> Export as PDF
                                                </a>
                                            </li> -->
                                            <li>
                                                <a class="dropdown-item" href="#" @click.prevent="printView">
                                                    <i class="bi bi-printer me-2"></i> Print
                                                </a>
                                            </li>

                                            <!-- Future Additions Example -->
                                            <template v-if="hasFutureFeatures">
                                                <li>
                                                    <hr class="dropdown-divider">
                                                </li>
                                                <li>
                                                    <h6 class="dropdown-header">
                                                        <i class="bi bi-plus-circle me-1"></i> Additional Features
                                                    </h6>
                                                </li>
                                                <li>
                                                    <a class="dropdown-item" href="#" @click.prevent="shareDocument">
                                                        <i class="bi bi-share me-2"></i> Share
                                                    </a>
                                                </li>
                                                <li>
                                                    <a class="dropdown-item" href="#" @click.prevent="sendReminder">
                                                        <i class="bi bi-bell me-2"></i> Send Reminder
                                                    </a>
                                                </li>
                                                <li>
                                                    <a class="dropdown-item" href="#" @click.prevent="createFollowUp">
                                                        <i class="bi bi-calendar-plus me-2"></i> Schedule Follow-up
                                                    </a>
                                                </li>
                                            </template>
                                        </ul>
                                    </div>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>

        <!-- Certificates & Permits -->
        <div class="form-section view-section">
            <div class="section-header">
                <div class="section-number">10</div>
                <h3>Cerificates</h3>
            </div>
            <div class="certs-card">
                <div class="cert-badges">
                    <span v-for="cert in loadedData.issuedCerts" :key="`cert${cert.ic_id}`"
                        @click="handleCertClick(cert.type)" class="cert-badge clickable-badge"
                        :title="`Click to view ${cert.type} certificate`">
                        <i class="bi bi-file-earmark-text me-1"></i>
                        {{ cert.type }}
                    </span>

                    <span v-if="loadedData.application.status === 'Active'" @click="handleCertClick('bpermit')"
                        class="cert-badge clickable-badge business-permit-badge" title="Click to view Business Permit">
                        <i class="bi bi-award me-1"></i>
                        Business Permit
                    </span>
                </div>
            </div>
        </div>

        <!-- Action Buttons (View Mode) -->
        <div class="view-mode-actions">
            <button @click="goBack" class="btn btn-back">
                <i class="bi bi-arrow-left"></i> Back to List
            </button>
            <!-- <button @click="printView" class="btn btn-print">
                <i class="bi bi-printer"></i> Print View
            </button>
            <button @click="downloadPDF" class="btn btn-download">
                <i class="bi bi-download"></i> Download PDF
            </button> -->
        </div>
    </div>
    <SpinnerBlock v-if="isProcessing && show" />
    <MediumSizedModal title="Add Arrears" :show="showModal" @close="toggleModal">
        <AddArrearsInput ref="addArrearsInputRef" @close="toggleModal" @add-arrears="hadnleAddArrears" />
    </MediumSizedModal>
    <MediumSizedModal title="SOA Details" :show="showSoaModal" @close="toggleSoaModal">
        <SoaDetailsView :soa="soa" />
    </MediumSizedModal>
    <FullscreenModal2 :title="getCertTitle" :show="showCertModal" @close="toggleCertModal">
        <OBOCert ref="oboCertRef" @save-success="handleSaveToIssueCertSuccess" @close="toggleCertModal"
            v-if="certType === 'obo'" :current-application="loadedData" />
        <LocationalCert @save-success="handleSaveToIssueCertSuccess" @close="toggleCertModal" fontSize="20px"
            ref="locationalCertRef" v-if="certType === 'loc'" :currentApplication="loadedData" />
        <SanitaryCert ref="sanitaryCertRef" @save-success="handleSaveToIssueCertSuccess" @close="toggleCertModal"
            v-if="certType === 'san'" :currentApplication="loadedData" />
        <BusinessPermit ref="businessPermitRef" v-if="certType === 'bpermit'" :currentApplication="loadedData" />
        <BrgyClearance ref="brgyClearanceRef" v-if="certType === 'brc'" :currentApplication="loadedData" />
    </FullscreenModal2>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import SpinnerBlock from '../animations/SpinnerBlock.vue';
import businessService from '@/services/businessService';
import { hasRoles } from '@/utils/auth';
import confirmationService from '@/services/confirmationService';
import toastService from '@/services/toastService';
import spinnerService from '@/services/spinnerService';
import MediumSizedModal from '../modals/MediumSizedModal.vue';
import AddArrearsInput from '../inputs/AddArrearsInput.vue';
import SoaDetailsView from './SoaDetailsView.vue';
import OBOCert from '../certs/OBOCert.vue';
import FullscreenModal2 from '../modals/FullscreenModal2.vue';
import LocationalCert from '../certs/LocationalCert.vue';
import SanitaryCert from '../certs/SanitaryCert.vue';
import BusinessPermit from '../certs/BusinessPermit.vue';
import BrgyClearance from '../certs/BrgyClearance.vue';
import businessApplicationPrinter from '@/utils/businessApplicationPrinter';
import paymentService from '@/services/paymentService';

//eslint-disable-next-line
const props = defineProps({
    id: {
        type: Number,
        default: 0
    },
    show: {
        type: Boolean,
        default: false
    },

});

const handlePayNow = async (bill) => {
    const confirmed = await confirmationService.show("You will be redirected to online payment portal. Are you sure you want to proceed?");
    if (!confirmed) return;
    const payload = {
        item_id: bill.bsoa_id.toString(),
        account_no: loadedData.value.application.bin,
        account_name: `${loadedData.value.permitInfo.owner_firstname} ${loadedData.value.permitInfo.owner_lastname}`,
        business_name: loadedData.value.permitInfo.business_name,
        amount: bill.soaAmount
    };
    spinnerService.show();
    const resp = await paymentService.initiateBPLSPayment(payload);
    if (resp.success) {
        window.location.href = resp.url;
    }
    else {
        spinnerService.hide();
    }
}
const handleCertClick = (cert) => {
    certType.value = cert;
    switch (cert.toLowerCase()) {
        case "brc":
            viewBrgyClearance();
            break;
        case "obo":
            viewOboClearance();
            break;
        case "san":
            viewSanitaryClearance();
            break;
        case "loc":
            viewLocationalClearance();
            break;
        case "bpermit":
            viewBusinessPermit();
            break;

    }
}
const certType = ref("");
const sanitaryCertRef = ref(null);
const locationalCertRef = ref(null);
const oboCertRef = ref(null);
const brgyClearanceRef = ref(null);
const businessPermitRef = ref(null);
const addArrearsInputRef = ref(null);
const soa = ref([]);
const showModal = ref(false);
const showSoaModal = ref(false);
const showCertModal = ref(false);
const getCertTitle = computed(() => {
    switch (certType.value) {
        case 'obo':
            return 'OBO Clearance'
        case 'loc':
            return 'Locational Clearance'
        case 'san':
            return 'Sanitary Clearance'
        case 'bpermit':
            return 'Business Permit'
        default:
            return 'Clearance'
    }
});
const toggleModal = () => {
    showModal.value = !showModal.value;
    if (showModal.value) {
        addArrearsInputRef.value.resetValues();
        addArrearsInputRef.value.focusToInput();
    }
}

const toggleSoaModal = () => {
    showSoaModal.value = !showSoaModal.value;
    if (showSoaModal.value) {
        soa.value = loadedData.value.soaBalances.find(x => x.bsoa_id === loadedData.value.soa.bsoa_id);
    }
}

const handleSaveToIssueCertSuccess = () => {
    toggleCertModal();
    getBusinessInfoWithoutAnimation(loadedData.value.application.application_id);
}

const toggleCertModal = () => {
    showCertModal.value = !showCertModal.value;
}

// Methods
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            const parts = dateString.split('/');
            if (parts.length === 3) {
                const [month, day, year] = parts;
                return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
            }
            return dateString;
        }

        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();

        return `${month}/${day}/${year}`;
    } catch (error) {
        return dateString;
    }
};


const viewOboClearance = () => {
    certType.value = 'obo';
    nextTick(() => {
        oboCertRef.value.getSignatories(certType.value);
    })
    toggleCertModal();
}

const viewBrgyClearance = () => {
    certType.value = 'brc';
    nextTick(() => {
        brgyClearanceRef.value.getBrgyClearanceImage();
    })
    toggleCertModal();
}

const viewLocationalClearance = () => {
    certType.value = 'loc';
    nextTick(() => {
        locationalCertRef.value.getSignatories(certType.value);
    });
    toggleCertModal();
}

const viewSanitaryClearance = () => {
    certType.value = 'san';
    nextTick(() => {
        sanitaryCertRef.value.getSignatories(certType.value);
    });
    toggleCertModal();
}

const viewBusinessPermit = () => {
    certType.value = 'bpermit';
    nextTick(() => {
        businessPermitRef.value.getSignatories(certType.value);
    })
    toggleCertModal();
}

const canViewSoa = computed(() => {
    if (!loadedData.value || !loadedData.value.soa || !loadedData.value.soa.bsoa_id)
        return false;
    return true;
})

const hadnleAddArrears = async (amount) => {
    toggleModal();
    spinnerService.show();
    const resp = await businessService.addArrears({ amount: amount, bsoa_id: loadedData.value.soa.bsoa_id });
    if (resp.success) {
        toastService.success("Arrears added successfully!");
        getBusinessInfoWithoutAnimation(loadedData.value.application.application_id);
    }
    else {
        toastService.error(resp.message);
    }
    spinnerService.hide();
}

const loadedData = ref({
    application: { application_id: 0 },
    permitInfo: { type: null },
    businessInfo: { type: null }
});
//eslint-disable-next-line
const onActionSelected = (action) => {
    switch (action) {
        case 'delete':
            deleteApplication();
            break;
        case 'reset':
            resetApplication();
            break;
        case 'edit':
            editApplication();
            break;
        case 'renew':
            emit('renew');
            break;
    }
}

const handleAddArrearsRequest = () => {
    toggleModal();
}

const editApplication = () => {
    emit('edit');
}


const deleteApplication = async () => {
    if (loadedData.value.application.application_id == 0)
        return;
    const confirmed = await confirmationService.show("This action cannot be undone. Are you sure you want to proceed?");
    if (!confirmed)
        return;
    spinnerService.show();
    const resp = await businessService.deleteApplication(loadedData.value.application.application_id);
    if (resp.success) {
        toastService.success("Application deleted successfully!");
        emit('close', true);
    }
    else {
        toastService.error(resp.message);
    }
    spinnerService.hide();
}

const resetApplication = async () => {
    if (loadedData.value.application.application_id == 0)
        return;
    const confirmed = await confirmationService.show("This action cannot be undone. Are you sure you want to proceed?");
    if (!confirmed)
        return;
    const resp = await businessService.resetApplication(loadedData.value.application.application_id);
    if (resp.success) {
        toastService.success("Application reset successful!");
        emit('close', true);
    }
    else {
        toastService.error(resp.message);
    }
}

const isProcessing = ref(false);
//eslint-disable-next-line
const emit = defineEmits(['back', 'print', 'download', 'close', 'edit', 'renew', 'reset'])
const canGenerateSoa = (bal, index) => {
    if (index > 0) {
        return bal.bsoa_id != loadedData.value.soa.bsoa_id && loadedData.value.soaBalances[index - 1].paid == 1 && bal.paid == 0 && bal.bsoa_id == 0 && hasRoles(['issuetop', 'super admin'])
    }
    else {
        return false;
    }
}

const generateSoaBalance = async (id) => {
    const confirmed = await confirmationService.show("Are you sure you want to generate soa items?");
    if (!confirmed) return;
    const resp = await businessService.generateSoaBalance(id);
    spinnerService.show();
    if (resp.success) {
        toastService.success("Soa created successfully!");
        getBusinessInfoWithoutAnimation(loadedData.value.application.application_id);
    }
    else {
        toastService.error(resp.message);
    }
    spinnerService.hide();
}
//eslint-disable-next-line
const canIssueTop = computed(() => {
    if (!loadedData.value.soa || (loadedData.value.soa.action === 'issued' && loadedData.value.soaBalances.length === 0))
        return true;
    return loadedData.value.soa && loadedData.value.soa.action == 'recompute' && hasRoles(['issuetop', 'superadmin']) && (loadedData.value.soaBalances[0]?.paid == 0 || !loadedData.value.soaBalances || loadedData.value.soaBalances.length === 0)
});

const canAddArrears = computed(() => {
    if (!loadedData.value)
        return false;
    if (loadedData.value.soa.paid == 1)
        return false;

    const currentSoaBalance = loadedData.value.soaBalances.find(x => x.bsoa_id === loadedData.value.soa.bsoa_id);
    if (!currentSoaBalance)
        return false;

    const idx = currentSoaBalance.details.findIndex(x => x.ltp_id === 1838);
    if (idx === -1)
        return true;

    return false;
});

const getBusinessInfo = async (id) => {
    if (!props.show)
        return;
    if (id > 0) {
        isProcessing.value = true;
        loadedData.value = await businessService.getBusinessInfo(id);
        loadedData.value.application = { ...loadedData.value.application, look_up_bin: loadedData.value.application.bin };
        isProcessing.value = false;
    }
};

const getBusinessInfoWithoutAnimation = async (id) => {
    if (!props.show)
        return;
    if (id > 0) {
        loadedData.value = await businessService.getBusinessInfo(id);
        loadedData.value.application = { ...loadedData.value.application, look_up_bin: loadedData.value.application.bin };
    }
};


const formatCurrency = (amount) => {
    if (!amount) return '0.00'
    return parseFloat(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })
}

const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return '?'
    const first = firstName ? firstName.charAt(0).toUpperCase() : ''
    const last = lastName ? lastName.charAt(0).toUpperCase() : ''
    return first + last
}

const formatFullName = (permitInfo) => {
    const parts = [
        permitInfo.owner_firstname,
        permitInfo.owner_middlename,
        permitInfo.owner_lastname,
        permitInfo.owner_suffix
    ].filter(Boolean)
    return parts.join(' ') || 'N/A'
}

const formatContactName = (contact) => {
    const parts = [
        contact.firstname,
        contact.middlename,
        contact.lastname,
        contact.suffix
    ].filter(Boolean)
    return parts.join(' ') || 'N/A'
}

const formatLocation = (permitInfo) => {
    const parts = [
        permitInfo.barangay,
        permitInfo.city,
        permitInfo.province,
        permitInfo.zip
    ].filter(Boolean)
    return parts.join(', ') || 'N/A'
}

const formatOwnerLocation = (permitInfo) => {
    const parts = [
        permitInfo.owner_barangay,
        permitInfo.owner_city,
        permitInfo.owner_province,
        permitInfo.owner_zip
    ].filter(Boolean)
    return parts.join(', ') || 'N/A'
}

const formatOperationLocation = (businessInfo) => {
    const parts = [
        businessInfo?.operation_barangay,
        businessInfo?.operation_city,
        businessInfo?.operation_province,
        businessInfo?.operation_zip
    ].filter(Boolean)
    return parts.join(', ') || 'N/A'
}

const getStatusClass = (status) => {
    if (!status) return 'status-default'
    const statusMap = {
        'Checking': 'status-checking',
        'Approved': 'status-approved',
        'Rejected': 'status-rejected',
        'Draft': 'status-draft',
        'Completed': 'status-completed'
    }
    return statusMap[status] || 'status-default'
}
// eslint-disable-next-line
defineExpose({
    getBusinessInfo
})

// Emit events
const goBack = () => emit('back')
const printView = () => {
    businessApplicationPrinter.print(loadedData.value, {
        logoUrl: '/favicon.ico', // Optional
        showWatermark: true,
        autoPrint: true,
        paperSize: 'A4'
    });
}
//const downloadPDF = () => emit('download')

watch(
    () => props.id,
    (newVal) => {
        if (newVal > 0) {
            //getBusinessInfo(newVal);
        }
    },
    { immediate: true }
);

</script>

<style scoped>
/* Option 1: Enhanced Badge Styles */
.cert-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding: 1rem;
}

.clickable-badge {
    cursor: pointer;
    padding: 8px 16px;
    border-radius: 20px;
    font-weight: 500;
    font-size: 0.875rem;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    background: linear-gradient(135deg, #28a745, #20c997);
    color: white;
    border: 2px solid transparent;
    box-shadow: 0 2px 8px rgba(40, 167, 69, 0.2);
}

.clickable-badge:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
    border-color: white;
    background: linear-gradient(135deg, #20c997, #28a745);
}

.clickable-badge:active {
    transform: translateY(0);
    box-shadow: 0 2px 5px rgba(40, 167, 69, 0.2);
}

.business-permit-badge {
    background: linear-gradient(135deg, #dc3545, #fd7e14);
    box-shadow: 0 2px 8px rgba(220, 53, 69, 0.2);
}

.business-permit-badge:hover {
    box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
    background: linear-gradient(135deg, #fd7e14, #dc3545);
}

/* Option 2: Button-style Badges */
.cert-buttons {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 1rem;
}

.btn-cert {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: white;
    border: 2px solid #e9ecef;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.3s ease;
    text-align: left;
    width: 100%;
}

.btn-cert:hover {
    border-color: #28a745;
    background: #f8fff9;
    transform: translateX(5px);
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.1);
}

.btn-cert:active {
    transform: translateX(2px);
}

.cert-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
}

.btn-cert .cert-icon {
    background: #e8f5e9;
    color: #28a745;
}

.business-permit .cert-icon {
    background: #ffeaea;
    color: #dc3545;
}

.cert-text {
    flex: 1;
    font-weight: 500;
    color: #2c3e50;
}

.cert-badge {
    background: #28a745;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
}

.cert-hint {
    color: #6c757d;
    opacity: 0.7;
    transition: all 0.3s ease;
}

.btn-cert:hover .cert-hint {
    opacity: 1;
    color: #28a745;
}

/* Option 3: Card-style Certificates */
.certs-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid #e9ecef;
}

.certs-title {
    color: #2c3e50;
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.certs-subtitle {
    color: #6c757d;
    font-size: 0.875rem;
    margin-bottom: 1.5rem;
}

.certificates-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1rem;
}

.certificate-card {
    background: white;
    border: 2px solid #e9ecef;
    border-radius: 10px;
    padding: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    display: flex;
    align-items: center;
    gap: 1rem;
}

.certificate-card:hover {
    border-color: #28a745;
    background: #f8fff9;
    box-shadow: 0 6px 20px rgba(40, 167, 69, 0.1);
    transform: translateY(-2px);
}

.certificate-card.highlight {
    border-color: #ffc107;
    background: #fffcf3;
}

.certificate-card.highlight:hover {
    border-color: #fd7e14;
    box-shadow: 0 6px 20px rgba(255, 193, 7, 0.2);
}

.certificate-icon {
    width: 50px;
    height: 50px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    background: #e8f5e9;
    color: #28a745;
}

.certificate-icon.highlight {
    background: #fff3cd;
    color: #ffc107;
}

.certificate-content {
    flex: 1;
}

.certificate-name {
    margin: 0;
    font-weight: 600;
    color: #2c3e50;
    font-size: 1rem;
}

.certificate-desc {
    margin: 0;
    font-size: 0.75rem;
    color: #6c757d;
}

.certificate-action {
    color: #adb5bd;
    transition: all 0.3s ease;
}

.certificate-card:hover .certificate-action {
    color: #28a745;
    transform: translateX(3px);
}

.certificate-notification {
    position: absolute;
    top: -5px;
    right: -5px;
}

.notification-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #dc3545;
    display: block;
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% {
        transform: scale(1);
        opacity: 1;
    }

    50% {
        transform: scale(1.2);
        opacity: 0.7;
    }

    100% {
        transform: scale(1);
        opacity: 1;
    }
}

/* Active/Selected State */
.certificate-card.selected {
    border-color: #007bff;
    background: #f0f8ff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

/* Loading State */
.certificate-card.loading {
    position: relative;
    overflow: hidden;
}

.certificate-card.loading::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    animation: loading 1.5s infinite;
}

@keyframes loading {
    0% {
        left: -100%;
    }

    100% {
        left: 100%;
    }
}

/* Responsive Design */
@media (max-width: 768px) {
    .certificates-grid {
        grid-template-columns: 1fr;
    }

    .cert-badges {
        justify-content: center;
    }

    .btn-cert {
        padding: 10px 14px;
    }
}

.form-info {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-radius: 20px;
    padding: 2rem;
    box-shadow: 0 20px 60px rgba(220, 53, 69, 0.1);
    border: 1px solid rgba(220, 53, 69, 0.1);
    backdrop-filter: blur(10px);
}

/* Header Styles */
.form-header {
    text-align: center;
    margin-bottom: 3rem;
    padding-bottom: 2rem;
    border-bottom: 2px solid rgba(220, 53, 69, 0.1);
    background: linear-gradient(135deg, #dc3545, #c82333);
    border-radius: 15px;
    padding: 2rem;
    color: white;
    margin: -2rem -2rem 3rem -2rem;
}

.form-header h2 {
    font-weight: 700;
    font-size: 2rem;
    margin-bottom: 0.5rem;
}

.form-header p {
    opacity: 0.9;
    font-size: 1.1rem;
}

.header-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
}

/* Section Styles */
.form-section {
    background: white;
    border-radius: 15px;
    padding: 2rem;
    margin-bottom: 2rem;
    box-shadow: 0 5px 25px rgba(220, 53, 69, 0.08);
    border: 1px solid rgba(220, 53, 69, 0.05);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.form-section:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(220, 53, 69, 0.12);
}

.section-header {
    display: flex;
    align-items: center;
    margin-bottom: 2rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid rgba(220, 53, 69, 0.1);
}

.section-number {
    background: linear-gradient(135deg, #dc3545, #c82333);
    color: white;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    margin-right: 1rem;
    font-size: 1.2rem;
}

.section-header h3 {
    color: #2c3e50;
    font-weight: 600;
    margin: 0;
}

/* Form Controls */
.form-group {
    margin-bottom: 0;
}

.form-label {
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 0.5rem;
    display: block;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.modern-input {
    border: 2px solid #e9ecef;
    border-radius: 12px;
    padding: 0.75rem 1rem;
    font-size: 1rem;
    transition: all 0.3s ease;
    background: white;
}

.modern-input:focus {
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
    transform: translateY(-1px);
}

.modern-input-group {
    border-radius: 12px;
    overflow: hidden;
}

.modern-input-group .input-group-text {
    background: linear-gradient(135deg, #dc3545, #c82333);
    border: none;
    color: white;
    font-weight: 500;
    padding: 0.75rem 1rem;
}

.modern-input-group .form-control {
    border: 2px solid #e9ecef;
    border-left: none;
}

.modern-input-group .form-control:focus {
    border-color: #dc3545;
    border-left: none;
}

/* Submit Button */
.form-actions {
    text-align: center;
    margin-top: 3rem;
    padding-top: 2rem;
    border-top: 2px solid rgba(220, 53, 69, 0.1);
}

.top-divider {
    border-top: 2px solid rgba(220, 53, 69, 0.1);
}

.btn-submit {
    background: linear-gradient(135deg, #dc3545, #c82333);
    border: none;
    border-radius: 50px;
    padding: 1rem 3rem;
    font-size: 1.1rem;
    font-weight: 600;
    color: white;
    transition: all 0.3s ease;
    box-shadow: 0 5px 20px rgba(220, 53, 69, 0.3);
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.btn-submit:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(220, 53, 69, 0.4);
    background: linear-gradient(135deg, #c82333, #a71e2a);
}

/* Transitions */
.slide-up-enter-active,
.slide-up-leave-active {
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from {
    opacity: 0;
    transform: translateY(30px);
}

.slide-up-leave-to {
    opacity: 0;
    transform: translateY(-30px);
}

/* Responsive Design */
@media (max-width: 768px) {
    .form-info {
        padding: 1rem;
        border-radius: 15px;
        margin: 0.5rem;
    }

    .form-header {
        margin: -1rem -1rem 2rem -1rem;
        padding: 1.5rem;
    }

    .form-header h2 {
        font-size: 1.5rem;
    }

    .form-section {
        padding: 1.5rem;
    }

    .section-header {
        flex-direction: column;
        text-align: center;
        gap: 1rem;
    }

    .btn-submit {
        width: 100%;
        justify-content: center;
    }
}

@media (max-width: 576px) {
    .form-info {
        padding: 1rem 0.5rem;
    }

    .form-section {
        padding: 1rem;
    }

    .row.g-3 {
        margin: 0;
    }

    .col-12 {
        padding: 0.25rem;
    }
}

/* Contact Input Card */
.contact-input-card {
    background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
    border: 2px dashed rgba(220, 53, 69, 0.2);
    border-radius: 16px;
    padding: 2rem;
    margin-bottom: 2rem;
    transition: all 0.3s ease;
}

.contact-input-card:hover {
    border-color: rgba(220, 53, 69, 0.4);
    box-shadow: 0 5px 20px rgba(220, 53, 69, 0.1);
}

/* Contact Actions */
.contact-actions {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 2px solid rgba(220, 53, 69, 0.1);
}

.btn-add-contact {
    background: linear-gradient(135deg, #dc3545, #c82333);
    color: white;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
}

.btn-add-contact:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(220, 53, 69, 0.4);
}

.btn-clear-contact {
    background: transparent;
    color: #6c757d;
    border: 2px solid #6c757d;
    padding: 0.75rem 1.5rem;
    border-radius: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.btn-clear-contact:hover {
    background: #6c757d;
    color: white;
    transform: translateY(-1px);
}

/* Contacts List Section */
.contacts-list-section {
    margin-top: 2rem;
}

.contacts-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid rgba(220, 53, 69, 0.1);
}

.contacts-header h4 {
    margin: 0;
    color: #2c3e50;
    font-weight: 600;
    font-size: 1.3rem;
}

.contacts-count {
    background: linear-gradient(135deg, #dc3545, #c82333);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 600;
}

/* Contacts Grid */
.contacts-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 1.5rem;
}

/* Contact Card */
.contact-card {
    background: white;
    border-radius: 16px;
    padding: 1.5rem;
    box-shadow: 0 5px 25px rgba(220, 53, 69, 0.08);
    border: 1px solid rgba(220, 53, 69, 0.05);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.contact-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 30px rgba(220, 53, 69, 0.12);
}

.contact-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(135deg, #dc3545, #c82333);
}

/* Contact Header */
.contact-header {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.contact-avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: linear-gradient(135deg, #dc3545, #c82333);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 700;
    font-size: 1.1rem;
    flex-shrink: 0;
}

.contact-info {
    flex: 1;
}

.contact-name {
    margin: 0 0 0.25rem 0;
    color: #2c3e50;
    font-weight: 600;
    font-size: 1.1rem;
    line-height: 1.3;
}

.contact-role {
    margin: 0;
    color: #6c757d;
    font-size: 0.85rem;
    background: #f8f9fa;
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    display: inline-block;
}

.btn-remove-contact {
    background: rgba(220, 53, 69, 0.1);
    border: none;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #dc3545;
    cursor: pointer;
    transition: all 0.3s ease;
    flex-shrink: 0;
}

.btn-remove-contact:hover {
    background: #dc3545;
    color: white;
    transform: scale(1.1);
}

/* Contact Details */
.contact-details {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.contact-detail {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0;
}

.contact-detail i {
    color: #dc3545;
    width: 16px;
    font-size: 0.9rem;
}

.contact-detail span {
    color: #495057;
    font-size: 0.95rem;
    font-weight: 500;
}

/* Contact Footer */
.contact-footer {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(220, 53, 69, 0.1);
    display: flex;
    justify-content: flex-end;
}

.contact-index {
    background: #f8f9fa;
    color: #6c757d;
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.8rem;
    font-weight: 600;
}

.view-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    border: 1px solid #e9ecef;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.summary-card {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 2rem;
}

.summary-row {
    display: flex;
    flex-wrap: wrap;
    gap: 2rem;
}

.summary-item {
    flex: 1;
    min-width: 200px;
}

.summary-label {
    display: block;
    font-size: 0.875rem;
    opacity: 0.9;
    margin-bottom: 0.25rem;
}

.summary-value {
    font-size: 1.25rem;
    font-weight: 600;
}

.status-badge {
    display: inline-block;
    padding: 0.25rem 1rem;
    border-radius: 20px;
    font-size: 0.875rem;
    font-weight: 600;
    margin-top: 0.5rem;
}

.status-checking {
    background: #ffc107;
    color: #000;
}

.status-approved {
    background: #28a745;
    color: white;
}

.status-rejected {
    background: #dc3545;
    color: white;
}

.status-draft {
    background: #6c757d;
    color: white;
}

.status-completed {
    background: #007bff;
    color: white;
}

.status-default {
    background: #e9ecef;
    color: #495057;
}

.info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.5rem;
    margin-top: 1rem;
}

.info-item {
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 8px;
}

.info-label {
    display: block;
    font-size: 0.875rem;
    color: #6c757d;
    margin-bottom: 0.25rem;
}

.info-value {
    font-size: 1rem;
    color: #212529;
    font-weight: 500;
    margin: 0;
}

.business-name {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2d3748;
}

.address-card {
    background: #f8f9fa;
    padding: 1.5rem;
    border-radius: 8px;
    margin-top: 1rem;
}

.address-line {
    margin: 0.25rem 0;
    color: #495057;
}

.owner-card,
.contact-card-view,
.lob-card {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 10px;
    padding: 1.5rem;
    margin-top: 1rem;
}

.owner-header,
.contact-header-view,
.lob-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
}

.owner-avatar,
.contact-avatar-view {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: bold;
}

.owner-name {
    margin: 0;
    font-size: 1.25rem;
    color: #2d3748;
}

.owner-citizenship {
    margin: 0;
    color: #6c757d;
}

.contacts-grid-view {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 1rem;
    margin-top: 1rem;
}

.contact-index-view {
    margin-left: auto;
    color: #6c757d;
    font-size: 0.875rem;
}

.contact-details-view {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.contact-detail-view {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #495057;
}

.operations-summary {
    background: #f8f9fa;
    padding: 1.5rem;
    border-radius: 8px;
    margin-top: 1rem;
}

.operations-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
}

.operation-item {
    text-align: center;
    padding: 1rem;
    background: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.operation-label {
    display: block;
    font-size: 0.875rem;
    color: #6c757d;
    margin-bottom: 0.5rem;
}

.operation-value {
    font-size: 1.5rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
}

.sub-section {
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 2px solid #e9ecef;
}

.sub-section h4 {
    color: #2d3748;
    margin-bottom: 1rem;
}

.lob-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 1rem;
}

.lob-type-badge {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
}

.main-badge {
    background: #28a745;
    color: white;
}

.other-badge {
    background: #6c757d;
    color: white;
}

.lob-title {
    flex: 1;
    margin: 0;
    color: #2d3748;
}

.lob-details {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e9ecef;
}

.lob-detail {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.lob-detail label {
    font-size: 0.875rem;
    color: #6c757d;
}

.capitalization {
    color: #28a745;
    font-weight: 600;
    font-size: 1.125rem;
}

.empty-state {
    text-align: center;
    padding: 3rem;
    color: #6c757d;
}

.empty-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
}

.view-mode-actions {
    display: flex;
    gap: 1rem;
    justify-content: center;
    padding: 2rem 0;
    border-top: 1px solid #e9ecef;
    margin-top: 2rem;
}

.btn {
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    border: none;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.3s ease;
}

.btn-back {
    background: #6c757d;
    color: white;
}

.btn-back:hover {
    background: #5a6268;
}

.btn-print {
    background: #17a2b8;
    color: white;
}

.btn-print:hover {
    background: #138496;
}

.btn-download {
    background: #28a745;
    color: white;
}

.btn-download:hover {
    background: #218838;
}

@media (max-width: 768px) {
    .form-info {
        padding: 1rem;
    }

    .summary-row,
    .operations-grid {
        grid-template-columns: 1fr;
    }

    .info-grid {
        grid-template-columns: 1fr;
    }

    .contacts-grid-view {
        grid-template-columns: 1fr;
    }

    .view-mode-actions {
        flex-direction: column;
    }

    .btn {
        width: 100%;
        justify-content: center;
    }
}


/* Responsive Design */
@media (max-width: 768px) {
    .contact-input-card {
        padding: 1.5rem;
    }

    .contact-actions {
        flex-direction: column;
        gap: 0.75rem;
    }

    .btn-add-contact,
    .btn-clear-contact {
        width: 100%;
        justify-content: center;
    }

    .contacts-grid {
        grid-template-columns: 1fr;
        gap: 1rem;
    }

    .contacts-header {
        flex-direction: column;
        gap: 1rem;
        text-align: center;
    }

    .contact-header {
        flex-direction: column;
        text-align: center;
        gap: 0.75rem;
    }

    .contact-avatar {
        align-self: center;
    }
}

@media (max-width: 576px) {
    .contact-input-card {
        padding: 1rem;
    }

    .contact-card {
        padding: 1.25rem;
    }

    .contact-details {
        gap: 0.5rem;
    }

    .contact-detail {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.25rem;
    }
}

/* Loading state */
.form-info:has(.modern-input:disabled) {
    opacity: 0.7;
    pointer-events: none;
}

/* Custom scrollbar */
.form-info::-webkit-scrollbar {
    width: 8px;
}

.form-info::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
}

.form-info::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #dc3545, #c82333);
    border-radius: 10px;
}

/* Action Menu Styles - Blends with current UI */
.action-menu-container {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 10;
}

.action-menu-btn {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #495057;
    font-size: 18px;
}

.action-menu-btn:hover {
    background: #e9ecef;
    border-color: #dee2e6;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.action-menu-dropdown {
    position: absolute;
    top: 50px;
    right: 0;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    min-width: 180px;
    overflow: hidden;
    z-index: 1000;
    animation: fadeInDown 0.2s ease;
}

@keyframes fadeInDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.action-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    width: 100%;
    text-align: left;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #495057;
    font-size: 14px;
    font-family: inherit;
}

.action-item:hover {
    background: #f8f9fa;
    color: #0d6efd;
}

.action-item i {
    font-size: 16px;
    width: 20px;
    text-align: center;
}

.action-divider {
    height: 1px;
    background: #e9ecef;
    margin: 4px 0;
}

.action-item-danger {
    color: #dc3545 !important;
}

.action-item-danger:hover {
    background: #fff5f5 !important;
    color: #dc3545 !important;
}

/* Responsive */
@media (max-width: 768px) {
    .action-menu-container {
        top: 15px;
        right: 15px;
    }

    .action-menu-dropdown {
        min-width: 160px;
    }
}
</style>